/**
 * preview_eval — client-executed handler (A5: the "ask the running app" leg of
 * the app-inspect triad — see / ask / read).
 *
 * "DevTools evaluate-script for the agent": run a JS snippet in the LIVE preview
 * iframe and return a bounded, structured-clone-safe view of the result — so the
 * agent can ASK the running app ("did papan.db.run return rows?", "is that node
 * in the DOM after render?") instead of guessing from source. Complements
 * preview_screenshot (SEE) and debug_log (READ).
 *
 * Same split as preview_screenshot / db_editor: the WORKER tool
 * (features/agent/tools/preview-eval.ts) is the SOLE advertiser + skill-gate
 * authority (requiredSkills: ['app-inspect']); this entry owns only the
 * browser-side EXECUTION + the AG-UI result channel. The handler runs against
 * the MODEL's streamed tool-call input (hooks.ts runClientTool → se.input), NOT
 * the worker's ctx.clientTool args.
 *
 * The actual eval happens INSIDE the preview iframe, in the injected bridge
 * (features/files/api/preview-snapshot.ts → getSnapshotScript). That bridge is
 * only INJECTED for editor / non-share previews and is OFF until an editable
 * author editor sends papan:enable-eval (same gate as the save-hotkey) — so on
 * read-only / share previews this handler gets a clean "not enabled" refusal (or
 * the code isn't there at all). The eval's power is bounded by the opaque-origin
 * sandbox + the parent-brokered SDK, not by this request (see the bridge's
 * EVAL_BRIDGE_SECTION comment). We only postMessage the request + await the reply.
 */
import { registerAgentTool, type AgentToolResult } from './registerAgentTool.js'
import { resolveReadyPreviewFrame, findActivePreviewFileId, toAbsoluteVfsPath, isPreviewReloading, previewStillMounting, previewCallCanResolve } from './previewIframe.js'
import { filesApi } from '@papan/files/client'
import { EVAL_TIMEOUT_MS, PREVIEW_EVAL_TIMEOUT_MESSAGE } from '../core/preview-eval-timeout.js'
import { readClientToolTarget } from '../core/client-tool-target.js'
import { frameMessageTarget, frameMessageFrom } from '@papan/collab/ui/app-origin-frame'

interface EvalArgs {
  code?: string
  path?: string
}

/** Shape the bridge replies with (papan:eval:result). */
interface EvalReply {
  ok?: boolean
  value?: unknown
  valueType?: string
  truncated?: boolean
  error?: string
  stack?: string
  ms?: number
  /** Set only on the parent-side ceiling path (not a bridge reply) so the worker
   *  can render a dedicated "timed out" line instead of "did not return a value". */
  timedOut?: boolean
  /** #385: the console shim's per-load session id, so the worker tool can read
   *  runtime errors captured for exactly this load. Absent if the preview has no
   *  console shim (non-design-eligible previews). */
  consoleSess?: string
}

/** Resolve a folder's entry HTML (index.html, else first root .html). */
async function resolveFolderEntryHtml(folderId: string): Promise<string | null> {
  const index = await filesApi.resolvePath('index.html', folderId).catch(() => null)
  if (index?.id) return index.id
  const listing = await filesApi.listChildren(folderId).catch(() => null)
  const html = listing?.files.find((f) => !f.isFolder && f.name.toLowerCase().endsWith('.html'))
  return html?.id ?? null
}

/** Post `papan:eval` to a specific, already-ready frame and await its reply. The
 *  reply is source-gated (only the frame we asked may answer). Resolves null on a
 *  post failure; resolves the reply otherwise (incl. the bridge's own ok:false
 *  errors so the caller can surface them verbatim). The frame is chosen by
 *  {@link resolveReadyPreviewFrame} FIRST — which pings to confirm the bridge is
 *  live — so this runs the (possibly side-effecting) snippet exactly once, on a
 *  buffer that will answer, instead of firing it at whichever iframe the DOM
 *  happens to call visible mid-swap. */
function postEval(cw: Window, code: string, target: string): Promise<EvalReply | null> {
  return new Promise<EvalReply | null>((resolve) => {
    const reqId = `agenteval_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const onMsg = (e: MessageEvent) => {
      if (!frameMessageFrom(e, cw, target)) return // only the frame we asked may answer (#1408 R6: at its origin)
      const d = e.data
      if (d && d.type === 'papan:eval:result' && d.reqId === reqId) {
        cleanup()
        resolve(d as EvalReply)
      }
    }
    const timer = setTimeout(() => {
      // NB: this only stops the PARENT waiting — the snippet keeps running in the
      // sandboxed preview (no cancellation). The message steers to a server-side
      // operation, which the app-scripts skill teaches idempotently, so a redo
      // after a partial loop-write does not double-count. See preview-eval-timeout.ts.
      cleanup()
      resolve({ ok: false, timedOut: true, error: PREVIEW_EVAL_TIMEOUT_MESSAGE })
    }, EVAL_TIMEOUT_MS)
    function cleanup() {
      clearTimeout(timer)
      window.removeEventListener('message', onMsg)
    }
    window.addEventListener('message', onMsg)
    try {
      cw.postMessage({ type: 'papan:eval', reqId, code }, target)
    } catch {
      cleanup()
      resolve(null)
    }
  })
}

async function handler(rawArgs: Record<string, unknown>): Promise<AgentToolResult> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { success: false, error: 'No browser context for preview_eval.' }
  }
  const args = rawArgs as EvalArgs
  const code = typeof args.code === 'string' ? args.code.trim() : ''
  if (!code) return { success: false, error: 'No `code` to evaluate.' }

  // Resolve which preview iframe to eval inside. The tab-viewer is keyed by
  // `tab.fileId` (TabContent.tsx) = the id of what's OPEN in the tab: a standalone
  // HTML file's id, or — for a `.miniapp` — the FOLDER id (NOT its entry HTML). So
  // try the resolved path's OWN id first (this is what matches a `.miniapp` folder
  // path against its open preview, AND a standalone `.html` path); only if THAT
  // isn't open and the path is a folder, fall back to the entry-HTML id (a
  // standalone HTML opened via its containing folder). The old code resolved a
  // folder STRAIGHT to entry-HTML, so a `.miniapp` folder path never matched its
  // own live preview — it reported "preview not open" even when it was.
  const candidates: string[] = []
  if (args.path && args.path.trim()) {
    const resolved = await filesApi.resolvePath(toAbsoluteVfsPath(args.path)).catch(() => null)
    if (!resolved?.id) {
      return { success: false, error: `No file or folder at "${args.path}". Pass the app's HTML file (or its .pdapp folder), or omit path to use the open preview.` }
    }
    candidates.push(resolved.id)
    if (resolved.file?.isFolder) {
      const entry = await resolveFolderEntryHtml(resolved.id)
      if (entry && entry !== resolved.id) candidates.push(entry)
    }
  } else {
    const active = findActivePreviewFileId(readClientToolTarget(rawArgs))
    if (!active) {
      return { success: false, error: 'No app preview is open and no path was given. Ask the user to open the app preview (or pass path = the HTML file / .pdapp folder), then try again.' }
    }
    candidates.push(active)
  }

  // Pick a buffer whose bridge is confirmed live (pings across the double-buffer
  // reload swap), THEN eval it once. `sawCandidate` lets us tell "reloading"
  // (a buffer was there but never answered — retryable) from "not open at all".
  const { frame, sawCandidate } = await resolveReadyPreviewFrame(candidates, { workspaceId: readClientToolTarget(rawArgs)?.workspaceId ?? null })
  const cw = frame?.contentWindow
  if (!cw) {
    return sawCandidate
      ? { success: false, error: 'The app preview is open but still loading or reloading — its bridge did not respond yet. Wait a moment and call preview_eval again (the app was mid-refresh, not closed).' }
      : { success: false, error: 'The app preview is not open (no live iframe to evaluate in). Ask the user to open the app preview and try again — preview_eval needs the running app, unlike db_op_check which is headless.' }
  }
  const reply = await postEval(cw, code, frameMessageTarget(frame))
  if (!reply) {
    return { success: false, error: 'The app preview is not open (no live iframe to evaluate in). Ask the user to open the app preview and try again — preview_eval needs the running app, unlike db_op_check which is headless.' }
  }
  if (!reply.ok) {
    // The bridge ran (or refused) — surface its error verbatim (runtime throw,
    // "not enabled", etc.). This is a REAL tool result, not a transport failure.
    // `timedOut` distinguishes the parent-side ceiling from a bridge error so the
    // worker can frame it as a timeout rather than "no value returned".
    return {
      success: true,
      data: { ok: false, error: reply.error ?? 'evaluation failed', stack: reply.stack ?? '', ms: reply.ms ?? null, timedOut: !!reply.timedOut, consoleSess: reply.consoleSess ?? '' },
    }
  }
  return {
    success: true,
    data: { ok: true, valueType: reply.valueType ?? 'unknown', value: reply.value, truncated: !!reply.truncated, ms: reply.ms ?? null, consoleSess: reply.consoleSess ?? '' },
  }
}

registerAgentTool('preview_eval', {
  transport: 'client-executed',
  // #356: route the LIVE dispatch through `client-tool-request` (mayDefer=true) so
  // `ready()` is consulted — with the default 'tool-call' path (mayDefer=false)
  // it is not, and the deferrable request is deduped away. Both worker tools
  // forward the model's args verbatim, so 'worker' changes nothing the handler
  // sees. `ready()` DEFERS while a preview reload is in flight (retry via /pending
  // until it settles) rather than fast-failing "preview not open" mid-swap; when
  // no reload is in flight it returns true so a genuinely-closed preview still
  // gets the handler's fast, actionable message (no indefinite wait).
  argsFrom: 'worker',
  ready: (args) => !isPreviewReloading() && !previewStillMounting(readClientToolTarget(args)),
  // Some page/app tabs never get a preview iframe until the user acts (an
  // uninstalled PDApp, Settings face, consent prompt, over-ceiling page): keep
  // R5's reload-remount defer but answer after 20 s instead of hanging.
  maxDeferMs: 20_000,
  // #352 I1: only a tab with the preview the handler would use claims the call first.
  canResolve: previewCallCanResolve,
  handler,
})

// Exported for unit tests (the handler is otherwise only reachable via the
// registry side-effect above).
export { handler as __previewEvalHandler }
